import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  RestaurantMenu as MenuIcon,
  Payment as PaymentIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";

// API endpoints
const PAYMENT_API = "https://okna.co.tz/mrms/Payment.php";
const MENU_API = "https://okna.co.tz/mrms/Menu.php";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
  '& svg': {
    fontSize: '2rem',
    color: theme.palette.primary.main
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  minWidth: 200,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: theme.spacing(2)
}));

export default function Performance() {
  // Helper to get local date string without timezone conversion
  const getLocalDateString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get start of day (00:00:00) for a given date
  const getStartOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Get end of day (23:59:59.999) for a given date
  const getEndOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const today = getLocalDateString(new Date());
  
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [menu, setMenu] = useState([]);
  const [reportType, setReportType] = useState("daily");

  const token = localStorage.getItem("token");

  // Helper function for authenticated fetch
  const authFetch = async (url, options = {}) => {
    if (!token) {
      throw new Error("No authentication token found");
    }
    
    // Add token to URL
    const separator = url.includes('?') ? '&' : '?';
    const urlWithToken = `${url}${separator}token=${token}`;
    
    const response = await fetch(urlWithToken, {
      ...options,
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers
      }
    });
    
    // Handle 401 Unauthorized
    if (response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("staff");
      window.location.href = "/";
      throw new Error("Session expired. Please login again.");
    }
    
    return response;
  };

  // Helper function to parse quantity strings like "012" to 12
  const parseQuantity = (qtyString) => {
    if (!qtyString && qtyString !== 0) return 0;
    const str = String(qtyString).trim();
    const num = parseInt(str, 10);
    return isNaN(num) ? 0 : num;
  };

  // Helper function to parse price strings
  const parsePrice = (priceString) => {
    if (!priceString && priceString !== 0) return 0;
    const str = String(priceString);
    const matches = str.match(/\d+\.?\d*/g);
    if (matches && matches.length > 0) {
      let total = 0;
      matches.forEach(match => {
        total += parseFloat(match) || 0;
      });
      return total;
    }
    return parseFloat(str) || 0;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Check token first
      if (!token) {
        throw new Error("No authentication token found. Please login again.");
      }

      // Fetch menu items with token
      console.log("Fetching menu...");
      const menuRes = await authFetch(MENU_API);
      let menuData = await menuRes.json();
      
      // Handle menu response
      if (menuData.error) {
        console.error("Menu API error:", menuData.error);
        menuData = [];
      } else if (!Array.isArray(menuData)) {
        console.error("Menu API returned non-array:", menuData);
        menuData = [];
      }
      
      setMenu(menuData);
      console.log(`Menu loaded: ${menuData.length} items`);

      // Fetch payments with token
      console.log("Fetching payments...");
      const payRes = await authFetch(PAYMENT_API);
      let payData = await payRes.json();
      
      // Handle payments response
      if (payData.error) {
        console.error("Payment API error:", payData.error);
        payData = [];
      } else if (!Array.isArray(payData)) {
        console.error("Payment API returned non-array:", payData);
        payData = [];
      }
      
      console.log(`Payments loaded: ${payData.length}`);

      // Create date range with full day coverage
      const startDateObj = getStartOfDay(startDate);
      const endDateObj = getEndOfDay(endDate);

      console.log("Date Range Filter:");
      console.log("Start (00:00:00):", startDateObj.toISOString());
      console.log("End (23:59:59):", endDateObj.toISOString());

      // Filter by date range and PAID status using full day range
      const filteredPayments = payData.filter(p => {
        if (!p.created_at || p.status !== "PAID") return false;
        
        const paymentDate = new Date(p.created_at);
        
        // Compare if payment date is within the full day range (inclusive)
        const isAfterOrEqualStart = paymentDate >= startDateObj;
        const isBeforeOrEqualEnd = paymentDate <= endDateObj;
        
        return isAfterOrEqualStart && isBeforeOrEqualEnd;
      });

      console.log("Filtered payments count:", filteredPayments.length);
      console.log("Filtered payments:", filteredPayments.map(p => ({
        id: p.order_id,
        order_number: p.order_number,
        created_at: p.created_at,
        status: p.status,
        staff_name: p.staff_name
      })));

      if (filteredPayments.length === 0) {
        setError(`No paid orders found between ${startDate} and ${endDate}`);
        setLoading(false);
        return;
      }

      // Process the data
      processReportData(filteredPayments, menuData);

    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const processReportData = (payments, menuData) => {
    // 1. Revenue by Payment Method
    const paymentMethods = {};
    let totalSales = 0;
    let totalOrders = payments.length;
    let totalItemsCount = 0;

    payments.forEach(payment => {
      const amount = parsePrice(payment.total_amount);
      totalSales += amount;
      
      const method = payment.payment_method || "CASH";
      if (!paymentMethods[method]) {
        paymentMethods[method] = {
          method,
          totalAmount: 0,
          count: 0
        };
      }
      paymentMethods[method].totalAmount += amount;
      paymentMethods[method].count++;
    });

    const paymentMethodsReport = Object.values(paymentMethods)
      .sort((a, b) => b.totalAmount - a.totalAmount);

    // 2. Items Sold Summary
    const itemsSold = {};

    payments.forEach(payment => {
      if (payment.quantity_total) {
        totalItemsCount += parseQuantity(payment.quantity_total);
      }
      
      if (payment.items) {
        let items = payment.items;
        if (typeof items === "string") {
          try {
            items = JSON.parse(items);
          } catch (e) {
            console.error("Error parsing items for payment", payment.order_id, e);
            items = [];
          }
        }

        if (Array.isArray(items)) {
          items.forEach(item => {
            const itemName = item.item_name || "Unknown Item";
            const menuItem = menuData.find(m => m && m.item_name === itemName);
            
            if (!itemsSold[itemName]) {
              itemsSold[itemName] = {
                name: itemName,
                category: menuItem?.group_button || "Uncategorized",
                quantity: 0,
                revenue: 0
              };
            }
            
            const quantity = parseQuantity(item.quantity);
            const subtotal = parsePrice(item.subtotal) || (parsePrice(item.price) * quantity);
            
            itemsSold[itemName].quantity += quantity;
            itemsSold[itemName].revenue += subtotal;
            
            if (!payment.quantity_total) {
              totalItemsCount += quantity;
            }
          });
        }
      }
    });

    const itemsSoldReport = Object.values(itemsSold)
      .sort((a, b) => b.revenue - a.revenue);

    // 3. Staff Performance - Count only orders created by each user (based on staff_name)
    const staffPerformance = {};

    payments.forEach(payment => {
      const amount = parsePrice(payment.total_amount);
      const itemsCount = parseQuantity(payment.quantity_total) || 0;

      // Only count orders created by staff_name (waitress/waiter)
      if (payment.staff_name && payment.staff_name !== "Unknown") {
        const staffName = payment.staff_name;
        if (!staffPerformance[staffName]) {
          staffPerformance[staffName] = {
            name: staffName,
            orderCount: 0,
            totalRevenue: 0,
            itemsServed: 0,
            percentage: 0,
            averageOrderValue: 0
          };
        }
        staffPerformance[staffName].orderCount++;
        staffPerformance[staffName].totalRevenue += amount;
        staffPerformance[staffName].itemsServed += itemsCount;
      }
    });

    // Calculate percentages and average order value
    Object.values(staffPerformance).forEach(staff => {
      staff.averageOrderValue = staff.orderCount > 0 ? (staff.totalRevenue / staff.orderCount) : 0;
      staff.percentage = totalSales > 0 ? (staff.totalRevenue / totalSales) * 100 : 0;
    });

    const staffPerformanceReport = Object.values(staffPerformance)
      .sort((a, b) => b.totalRevenue - a.totalRevenue);

    // Summary
    const averageOrderValue = totalOrders > 0 ? (totalSales / totalOrders) : 0;

    setReport({
      dateRange: { start: startDate, end: endDate },
      paymentMethods: paymentMethodsReport,
      items: itemsSoldReport,
      staffPerformance: staffPerformanceReport,
      summary: {
        totalSales,
        totalOrders,
        totalItems: totalItemsCount,
        averageOrderValue
      }
    });
  };

  useEffect(() => {
    // Don't auto-fetch, wait for user
  }, []);

  // Export to Excel - Only 3 sheets: Summary, Items Report, Staff Performance
  const exportExcel = () => {
    if (!report) return;

    const wb = XLSX.utils.book_new();

    // Sheet 1: Summary with Payment Methods
    const summaryData = [
      ["THE GENERAL REPORT"],
      [`Period: ${report.dateRange.start} to ${report.dateRange.end}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ["SUMMARY STATISTICS"],
      ["Total Sales (TZS)", report.summary.totalSales.toLocaleString()],
      ["Total Orders", report.summary.totalOrders],
      ["Total Items Sold", report.summary.totalItems],
      ["Average Order Value (TZS)", report.summary.averageOrderValue.toFixed(2)],
      [],
      ["REVENUE BY PAYMENT METHOD"],
      ["Payment Method", "Total Amount (TZS)", "Number of Orders", "Percentage"],
      ...report.paymentMethods.map(p => [
        p.method,
        p.totalAmount.toLocaleString(),
        p.count,
        report.summary.totalSales > 0 ? ((p.totalAmount / report.summary.totalSales) * 100).toFixed(2) + "%" : "0%"
      ])
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

    // Sheet 2: Items Sold Report
    if (report.items.length > 0) {
      const itemsData = [
        ["ITEMS SOLD (HIGH TO LOW REVENUE)"],
        ["Rank", "Item Name", "Category", "Quantity", "Revenue (TZS)", "Percentage"],
        ...report.items.map((item, index) => [
          index + 1,
          item.name,
          item.category,
          item.quantity,
          item.revenue.toLocaleString(),
          report.summary.totalSales > 0 ? ((item.revenue / report.summary.totalSales) * 100).toFixed(2) + "%" : "0%"
        ])
      ];
      const itemsSheet = XLSX.utils.aoa_to_sheet(itemsData);
      XLSX.utils.book_append_sheet(wb, itemsSheet, "Items Report");
    }

    // Sheet 3: Staff Performance
    if (report.staffPerformance.length > 0) {
      const totalPercentage = report.staffPerformance.reduce((sum, s) => sum + s.percentage, 0);
      
      const staffData = [
        ["STAFF PERFORMANCE (HIGH TO LOW REVENUE)"],
        ["Staff Name", "Orders", "Revenue (TZS)", "Items Served", "Avg Order (TZS)", "Percentage of Total Sales"],
        ...report.staffPerformance.map(s => [
          s.name,
          s.orderCount,
          s.totalRevenue.toLocaleString(),
          s.itemsServed,
          s.averageOrderValue.toFixed(2),
          s.percentage.toFixed(2) + "%"
        ]),
        [],
        ["TOTAL:", report.staffPerformance.reduce((sum, s) => sum + s.orderCount, 0), report.summary.totalSales.toLocaleString(), report.summary.totalItems, "", totalPercentage.toFixed(2) + "%"]
      ];
      const staffSheet = XLSX.utils.aoa_to_sheet(staffData);
      XLSX.utils.book_append_sheet(wb, staffSheet, "Staff Performance");
    }

    XLSX.writeFile(wb, `General_Report_${report.dateRange.start}_to_${report.dateRange.end}.xlsx`);
  };

  const handleReportTypeChange = (type) => {
    setReportType(type);
    const todayDate = new Date();
    const localToday = getLocalDateString(todayDate);
    
    switch(type) {
      case "daily":
        setStartDate(localToday);
        setEndDate(localToday);
        break;
      case "weekly":
        const weekAgo = new Date(todayDate);
        weekAgo.setDate(todayDate.getDate() - 7);
        setStartDate(getLocalDateString(weekAgo));
        setEndDate(localToday);
        break;
      case "monthly":
        const monthAgo = new Date(todayDate);
        monthAgo.setMonth(todayDate.getMonth() - 1);
        setStartDate(getLocalDateString(monthAgo));
        setEndDate(localToday);
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <StyledPaper>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: "bold", color: "#333" }}>
            <AssessmentIcon sx={{ fontSize: 40, mr: 2, verticalAlign: "middle", color: "#667eea" }} />
            THE GENERAL REPORT
          </Typography>

          <Button
            variant="contained"
            size="large"
            startIcon={<DownloadIcon />}
            onClick={exportExcel}
            disabled={!report}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)"
              }
            }}
          >
            Export to Excel
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => handleReportTypeChange(e.target.value)}
              >
                <MenuItem value="daily">Daily Report</MenuItem>
                <MenuItem value="weekly">Weekly Report</MenuItem>
                <MenuItem value="monthly">Monthly Report</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DatePicker
                    label="Start Date"
                    value={new Date(startDate)}
                    onChange={(newValue) => {
                      if (newValue) {
                        setStartDate(getLocalDateString(newValue));
                      }
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="End Date"
                    value={new Date(endDate)}
                    onChange={(newValue) => {
                      if (newValue) {
                        setEndDate(getLocalDateString(newValue));
                      }
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              onClick={fetchData}
              disabled={loading}
              sx={{ height: "56px", width: "100%" }}
            >
              {loading ? <CircularProgress size={24} /> : "Generate Report"}
            </Button>
          </Grid>
        </Grid>

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!report && !loading && !error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a date range and click "Generate Report" to view data.
          </Alert>
        )}

        {report && report.summary.totalOrders > 0 && (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard>
                  <CardContent>
                    <Typography color="inherit" gutterBottom>Total Sales</Typography>
                    <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
                      TZS {report.summary.totalSales.toLocaleString()}
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard sx={{ background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" }}>
                  <CardContent>
                    <Typography color="inherit" gutterBottom>Total Orders</Typography>
                    <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
                      {report.summary.totalOrders}
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard sx={{ background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}>
                  <CardContent>
                    <Typography color="inherit" gutterBottom>Items Sold</Typography>
                    <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
                      {report.summary.totalItems}
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard sx={{ background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)" }}>
                  <CardContent>
                    <Typography color="inherit" gutterBottom>Avg Order Value</Typography>
                    <Typography variant="h5" component="div" sx={{ fontWeight: "bold" }}>
                      TZS {report.summary.averageOrderValue.toFixed(2)}
                    </Typography>
                  </CardContent>
                </StatCard>
              </Grid>
            </Grid>

            {/* Payment Methods Section */}
            {report.paymentMethods.length > 0 && (
              <>
                <SectionHeader>
                  <PaymentIcon />
                  <Typography variant="h5">1. Revenue by Payment Method</Typography>
                </SectionHeader>
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableRow>
                        <TableCell><strong>Payment Method</strong></TableCell>
                        <TableCell align="right"><strong>Amount (TZS)</strong></TableCell>
                        <TableCell align="right"><strong>Orders</strong></TableCell>
                        <TableCell align="right"><strong>Percentage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.paymentMethods.map((row) => (
                        <TableRow key={row.method}>
                          <TableCell>{row.method}</TableCell>
                          <TableCell align="right">{row.totalAmount.toLocaleString()}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                          <TableCell align="right">
                            {((row.totalAmount / report.summary.totalSales) * 100).toFixed(2)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Items Sold Section */}
            {report.items.length > 0 && (
              <>
                <SectionHeader>
                  <MenuIcon />
                  <Typography variant="h5">2. Items Sold (High to Low Revenue)</Typography>
                </SectionHeader>
                <TableContainer component={Paper} sx={{ mb: 4, maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Rank</strong></TableCell>
                        <TableCell><strong>Item Name</strong></TableCell>
                        <TableCell><strong>Category</strong></TableCell>
                        <TableCell align="right"><strong>Quantity</strong></TableCell>
                        <TableCell align="right"><strong>Revenue (TZS)</strong></TableCell>
                        <TableCell align="right"><strong>Percentage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.items.map((row, index) => (
                        <TableRow key={row.name}>
                          <TableCell>#{index + 1}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.category}</TableCell>
                          <TableCell align="right">{row.quantity}</TableCell>
                          <TableCell align="right">{row.revenue.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            {((row.revenue / report.summary.totalSales) * 100).toFixed(2)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Staff Performance Section */}
            {report.staffPerformance.length > 0 && (
              <>
                <SectionHeader>
                  <PeopleIcon />
                  <Typography variant="h5">3. Staff Performance (High to Low Revenue)</Typography>
                </SectionHeader>
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                      <TableRow>
                        <TableCell><strong>Staff Name</strong></TableCell>
                        <TableCell align="right"><strong>Orders</strong></TableCell>
                        <TableCell align="right"><strong>Revenue (TZS)</strong></TableCell>
                        <TableCell align="right"><strong>Items</strong></TableCell>
                        <TableCell align="right"><strong>Avg Order (TZS)</strong></TableCell>
                        <TableCell align="right"><strong>Percentage</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.staffPerformance.map((row) => (
                        <TableRow key={row.name}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: "#667eea" }}>
                                {row.name ? row.name.charAt(0).toUpperCase() : "?"}
                              </Avatar>
                              {row.name}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{row.orderCount}</TableCell>
                          <TableCell align="right">{row.totalRevenue.toLocaleString()}</TableCell>
                          <TableCell align="right">{row.itemsServed}</TableCell>
                          <TableCell align="right">{row.averageOrderValue.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={row.percentage.toFixed(2) + "%"}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </StyledPaper>
    </Box>
  );
}